import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Divider, Grid, Paper, Skeleton, Stack } from '@mui/material'
import DropDownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { useStyles } from './EditorPageStatistics'
import { NetworkType } from '@professocialApi/professocial-core-frontend-api'
import { useDispatch, useSelector } from 'react-redux'
import {
  DeferredPost,
  DeferredPostStatus,
  FindPostsSearchType,
  FindPostsSortMode,
  PostTemplate
} from '@3m5/sm_publish-frontend-api'
import { StatisticConfiguration } from '@professocial/professocialTypes/types/configurationTypes'
import { ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import {
  findPostTemplatesByMetricInitiated,
} from '../../store/statistics/statistics.editor.actions'
import {
  getPostTemplatesByMetric
} from '../../store/statistics/statistics.editor.selector'
import ImportProfessocialPost
  from '@professocial/professocialCore/src/shared/components/ImportComponents/ImportProfessocialPost'
import { getTheme, namedRequestStatus } from '../../store/common/common.selectors'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import { FindPostTemplatesByMetricRequest } from '@3m5/sm_publish-frontend-api'
import { StatisticsRootState } from '../../store/statisticsRoot.reducer'
import addTimezone from '@professocial/professocialPosting/src/util/addTimezone'
import Statistic from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/Statistic'
import DashboardNetworkContentPostDetailsTable
  from '@professocial/professocialCore/src/screens/Editor/EditorDashboard/components/DashboardNetworkContentPostDetailsTable'

dayjs.extend(utc)

interface IProps {
  user: any
  type: FindPostsSearchType
  statisticConfiguration?: StatisticConfiguration
}

type PageFilterOptionSort = { key: FindPostsSortMode, value: string }

export interface PageFilterOptionProps {
  date: DropDownElement[]
  network: DropDownElement[]
  type: FindPostsSearchType
  sort: PageFilterOptionSort[]
}

export interface PageFilterChosenOptionProps {
  date: string
  sort: string
  network: string
}

const EditorSortedPosts: React.FC<IProps> = (props) => {
  const theme = useSelector(getTheme)
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()

  const [dateFrom, setDateFrom] = useState<Dayjs>(dayjs().startOf('year'))
  const [dateTo, setDateTo] = useState<Dayjs>(dayjs())

  const dispatchFindPostTemplatesByMetric = useCallback((params: FindPostTemplatesByMetricRequest) => dispatch(findPostTemplatesByMetricInitiated(params)), [dispatch])

  const postTemplatesByMetric = useSelector(getPostTemplatesByMetric)
  const statusOnPostTemplates: string | undefined = useSelector((state: StatisticsRootState) => namedRequestStatus(state, findPostTemplatesByMetricInitiated.type))

  const getYear = (years: number) => dayjs().subtract(years, 'year').year().toString()
  const filterOptions: PageFilterOptionProps = {
    type: props.type,
    sort: [
      { key: FindPostsSortMode.EngagementRate, value: t('statisticsCommon:engagementRate') },
      { key: FindPostsSortMode.RelativeReach, value: t('statisticsCommon:reachedFansPercent') },
    ],
    date: [
      { key: getYear(0), value: getYear(0) },
      { key: getYear(1), value: getYear(1) },
      { key: getYear(2), value: getYear(2) },
    ],
    network: [
      { key: NetworkType.Facebook, value: t('statisticsCommon:facebookNetwork') },
      { key: NetworkType.Instagram, value: t('statisticsCommon:instagramNetwork') },
    ],
  }

  const [chosenFilterOption, setChosenFilterOption] = useState<PageFilterChosenOptionProps>(
    {
      date: getYear(0),
      sort: FindPostsSortMode.EngagementRate,
      network: NetworkType.Facebook,
    },
  )

  const getPostsData = () => {
    const sameYear = chosenFilterOption.date === dayjs(dateTo).year().toString()
    if (sameYear) {
      dispatchFindPostTemplatesByMetric({
        findPostSearchType: filterOptions.type,
        findPostsSortMode: chosenFilterOption.sort as FindPostsSortMode,
        from: addTimezone(dateFrom.toDate()),
        to: addTimezone(dateTo.toDate()),
        limit: 20,
      })
    }
  }

  const countDeferredPostsAwaitingUserConfirmation = (postTemplate: PostTemplate): number => {
    const filter = postTemplatesByMetric?.deferredPosts.filter(
      (deferredPost: DeferredPost) =>
        deferredPost.templateId === postTemplate.id &&
        deferredPost.status === DeferredPostStatus.AwaitingUserConfirmation
    )
    return filter?.length || 0
  }

  const countOtherDeferredPosts = (postTemplate: PostTemplate): number => {

    const filter = postTemplatesByMetric?.deferredPosts.filter(
      (deferredPost: DeferredPost) =>
        deferredPost.templateId === postTemplate.id &&
        deferredPost.status !== DeferredPostStatus.TokenInvalid &&
        deferredPost.status !== DeferredPostStatus.ErrorWhilePosting &&
        deferredPost.status !== DeferredPostStatus.AwaitingUserConfirmation
    )
    return filter?.length || 0
  }

  useEffect(() => {
    getPostsData()
  }, [])

  useEffect(() => {
    getPostsData()
  }, [dateFrom, dateTo, chosenFilterOption])

  useEffect(() => {
    const thisYear = chosenFilterOption.date === dayjs().year().toString()
    setDateTo(thisYear ? dayjs() : dayjs().year(parseInt(chosenFilterOption.date)).endOf('year'))
    setDateFrom(dayjs().year(parseInt(chosenFilterOption.date)).startOf('year'))
  }, [chosenFilterOption.date])

  const preloaderSkeleton = (
    <>
      {[...Array(10)].map((x, i) =>
        <Paper key={i} sx={{ mb: 1 }}>
          <Grid container>
            <Grid xs={12} md={7} lg={8} sx={{ p: 4 }} container item>
              <Grid item xs='auto'>
                <Skeleton sx={{ mr: '13px' }} variant='circular' width={40} height={40} />
              </Grid>
              <Grid xs item>
                <Stack
                  direction='row'
                  justifyContent={{ xs: 'center', sm: 'flex-start' }}
                >
                  <Stack direction='column'>
                    <Skeleton width='50%' height={20} />
                    <Skeleton width={225} height={20} />
                  </Stack>
                  <Box marginLeft='auto' marginRight='20px'><Skeleton width='130px' height={20} /></Box>
                  <Box><Skeleton width='130px' height={20} /></Box>
                </Stack>
                <Grid xs={12} item>
                  <Skeleton sx={{ mt: 3 }} variant='rectangular' width='100%' height='300px' />
                  <Skeleton sx={{ mt: 3 }} width='100%' height={20} />
                  <Skeleton sx={{ mt: 1 }} width='90%' height={20} />
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={12} md={5} lg={4} container item sx={{ backgroundColor: '#F9F9F9' }}>
              <Grid sx={{ p: 4 }} xs item>
                <Stack direction='column' spacing={2}>
                  <Skeleton width='100%' height={20} />
                  <Skeleton sx={{ mt: 3 }} variant='rectangular' width='100%' height='50px' />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Paper>,
      )}
    </>
  )

  return (
    <Grid container>
      <Grid item xs={12}>
        <h3>{props.type === FindPostsSearchType.BestPosts ? t('statisticsCommon:successfulPosts') : t('statisticsCommon:unsuccessfulPosts')}</h3>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Box className={classes.boxItem}>
            <DropDownComponent
              options={filterOptions.date}
              label={t('statisticsCommon:dateRange')}
              value={chosenFilterOption.date}
              size='small'
              onChange={option => setChosenFilterOption({ ...chosenFilterOption, date: option.key })}
              disableClearable
            />
          </Box>
          <Box className={classes.boxItem}>
            <DropDownComponent
              options={filterOptions.sort}
              label={t('statisticsCommon:sort')}
              value={chosenFilterOption.sort}
              size='small'
              onChange={option => setChosenFilterOption({ ...chosenFilterOption, sort: option.key })}
              disableClearable
            />
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        {chosenFilterOption.network === NetworkType.Facebook && (
          <>
            {!!postTemplatesByMetric && !!postTemplatesByMetric.templates.length
              ? postTemplatesByMetric?.templates.map((postTemplate, index) => {
                const statisticsValues = {
                  postViews: postTemplate.views,
                  postImpressions: postTemplate.impressionsTotal,
                  postClicks: postTemplate.clicks,
                  postReactionsLikeTotal: postTemplate.likes,
                  shares: postTemplate.shares,
                  commentCount: postTemplate.comments,
                  postEngagedUsers: postTemplate.engagementRate * 100,
                  postEngagedFan: postTemplate.relativeReach * 100,
                }
                return (
                  <Paper key={index} className={classes.paper}>
                    <Grid container sx={{ position: 'relative' }}>
                      <ImportProfessocialPost
                        postConfiguration={{
                          theme: theme,
                          userRole: props.user.role,
                          size: ScreenEnum.xs,
                          view: ViewEnum.template,
                          post: postTemplate,
                        }}
                      />
                      <Grid container item spacing={2}>
                        <Grid item xs={12}><Divider/></Grid>
                        <Grid
                          container
                          item
                          xs={12}
                          md={6}
                          paddingTop={0}
                          sx={{ paddingTop: 0 }}
                        >
                          <DashboardNetworkContentPostDetailsTable
                            type='general'
                            title={t('coreCalendar:autoPublications')}
                            pending={countOtherDeferredPosts(postTemplate)}
                            successful={postTemplate.automaticSuccess}
                            failure={postTemplate.automaticFail}
                          />
                        </Grid>
                        <Grid
                          container
                          item
                          xs={12} md={6}
                          alignItems="flex-end"
                          justifyContent="flex-end"
                          paddingTop={0}
                        >
                          <DashboardNetworkContentPostDetailsTable
                            type='awaiting'
                            title={t('coreCalendar:individualPublications')}
                            pending={countDeferredPostsAwaitingUserConfirmation(postTemplate)}
                            successful={postTemplate.manualSuccess}
                            failure={postTemplate.manualFail}
                          />
                        </Grid>
                        <Grid item xs={12}><Divider/></Grid>
                        <Grid item xs={12}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                            justifyContent="space-between">
                            <Box display="flex">
                              <Statistic
                                networkPageId=""
                                post={postTemplate}
                                view={ViewEnum.single}
                                justifyContent="start"
                                networkType={NetworkType.Facebook}
                                value={statisticsValues}
                              />
                            </Box>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                )
              })
              : statusOnPostTemplates === 'success'
                ? <NoDataComponent noDataText={t('postingCommon:noPostings')} alignment="center" margin="16px 0"/>
                : preloaderSkeleton}
          </>
        )}
      </Grid>
    </Grid>

  )
}
export default EditorSortedPosts
